import {
  isRecoverableTerminalStatus,
  type PaymentStatus,
} from '../lib/stateChannel';

interface StatusBannerProps {
  status: PaymentStatus;
  message?: string;
  configuration?: any; // To allow dynamic coloring or text from Config
}

export function StatusBanner({ status, message, configuration }: StatusBannerProps) {
  if (status === 'pending') return null; // No banner if nothing has started processing

  const configColors = configuration?.AdditionalConfig?.Theme || {};

  const getStatusConfig = () => {
    switch (status) {
      case 'processing':
        return {
          bg: configColors.ProcessingBg || '#eff6ff', // blue-50
          border: configColors.ProcessingBorder || '#3b82f6', // blue-500
          text: configColors.ProcessingText || '#1e40af', // blue-800
          icon: (
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          ),
          defaultMessage: 'Procesando pago, por favor espera...'
        };
      case 'approved':
        return {
          bg: configColors.SuccessBg || '#f0fdf4', // green-50
          border: configColors.SuccessBorder || '#22c55e', // green-500
          text: configColors.SuccessText || '#166534', // green-800
          icon: (
            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ),
          defaultMessage: '¡Pago Aprobado!'
        };
      case 'refused':
        return {
          bg: configColors.ErrorBg || '#fef2f2', // red-50
          border: configColors.ErrorBorder || '#ef4444', // red-500
          text: configColors.ErrorText || '#991b1b', // red-800
          icon: (
            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ),
          defaultMessage: 'Pago Rechazado. Intenta con otro medio de pago.'
        };
      case 'expired':
        return {
          bg: configColors.WarningBg || '#fffbeb', // amber-50
          border: configColors.WarningBorder || '#f59e0b', // amber-500
          text: configColors.WarningText || '#92400e', // amber-800
          icon: (
            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          defaultMessage: 'Tiempo de espera expirado.'
        };
      case 'cancelled':
        return {
          bg: configColors.WarningBg || '#fffbeb',
          border: configColors.WarningBorder || '#f59e0b',
          text: configColors.WarningText || '#92400e',
          icon: (
            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ),
          defaultMessage: 'El flujo de pago fue cancelado.'
        };
      case 'error':
        return {
          bg: configColors.ErrorBg || '#fef2f2',
          border: configColors.ErrorBorder || '#ef4444',
          text: configColors.ErrorText || '#991b1b',
          icon: null,
          defaultMessage: 'No se pudo completar el flujo de pago.'
        };
      default:
        return {
          bg: '#f3f4f6', border: '#9ca3af', text: '#374151',
          icon: null, defaultMessage: 'Error desconocido.'
        };
    }
  };

  const UI = getStatusConfig();
  const recoveryMessage = isRecoverableTerminalStatus(status)
    ? status === 'expired'
      ? 'Este intento venció y ya no puede confirmarse. Volvé al comercio e iniciá un nuevo intento.'
      : 'Este intento terminó sin aprobación. Volvé al comercio e iniciá un nuevo intento.'
    : null;

  return (
    <div 
      className="mt-4 p-4 rounded-lg flex items-center border shadow-sm transition-all duration-300 ease-in-out"
      style={{ backgroundColor: UI.bg, borderColor: UI.border, color: UI.text }}
      role="alert"
      aria-live="polite"
      data-payment-terminal={recoveryMessage ? status : undefined}
    >
      {UI.icon}
      <div>
        <span className="font-medium">
          {message || UI.defaultMessage}
        </span>
        {recoveryMessage && (
          <p className="mt-1 text-sm">
            {recoveryMessage}
          </p>
        )}
      </div>
    </div>
  );
}
